// export.ts
// All export formats for Desk Reject evaluation results.
//
// Formats:
//   exportMarkdown        — improved markdown reading plan with time estimates
//   exportObsidianVault   — ZIP of individual .md files with YAML frontmatter
//   exportNotionCSV       — CSV formatted for Notion database import
//   exportCSV             — standard CSV for Excel / Sheets / systematic review
//   exportReadingPlan     — alias for exportMarkdown (backwards compat)
//
// Reading time estimates are calculated from word count + complexity + what_to_read.
// These are also exported as a standalone utility: estimateReadingTime()

import JSZip from 'jszip';
import type { EvaluationResult, Verdict, PaperType, WhatToRead } from '../types';
import {
  VERDICT_LABELS,
  PAPER_TYPE_LABELS,
  PAPER_TYPE_SHORT,
  WHAT_TO_READ_LABELS,
  WHAT_TO_READ_SHORT,
} from './formatters';

// ---------------------------------------------------------------------------
// Reading time estimation
// ---------------------------------------------------------------------------

export interface ReadingTimeEstimate {
  minutes: number;
  label: string;         // e.g. "~45 min"
  hoursLabel: string;    // e.g. "45 min" or "1 hr 20 min"
}

/**
 * Estimates reading time based on word count, technical complexity,
 * and the recommended reading depth (full / methods only / abstract / skip).
 *
 * Baseline: academic reading ~200 wpm for dense material, ~300 wpm for readable.
 * Adjusted down for high equation count (slows reading significantly).
 */
export function estimateReadingTime(result: EvaluationResult): ReadingTimeEstimate {
  const { word_count, equation_count, page_count } = result.stats;
  const readabilityScore = result.readability?.score ?? 3;

  // Base reading speed (wpm) — lower for technical papers
  const technicalPenalty = Math.max(0, (equation_count / Math.max(page_count, 1)) * 2);
  const readabilityBonus = (readabilityScore - 3) * 15; // ±15 wpm per readability point
  const baseWpm = Math.max(120, 220 + readabilityBonus - technicalPenalty);

  // Full read time
  const fullMinutes = Math.round(word_count / baseWpm);

  // Adjust for recommended reading depth
  let minutes: number;
  switch (result.what_to_read) {
    case 'full':
      minutes = fullMinutes;
      break;
    case 'methodology_only':
      // Methods section is roughly 20-30% of a paper
      minutes = Math.round(fullMinutes * 0.25);
      break;
    case 'abstract_only':
      // Abstract is ~250 words at full speed
      minutes = Math.max(3, Math.round(250 / 300));
      break;
    case 'skip':
      minutes = 0;
      break;
    default:
      minutes = fullMinutes;
  }

  // Add note-taking overhead for must_read / worth_it papers (1.4x)
  if (result.verdict === 'must_read' || result.verdict === 'worth_it') {
    minutes = Math.round(minutes * 1.4);
  }

  const label = minutes === 0 ? 'Skip' : `~${minutes} min`;
  const hoursLabel = formatMinutes(minutes);

  return { minutes, label, hoursLabel };
}

/** Formats minutes into human-readable duration */
function formatMinutes(minutes: number): string {
  if (minutes === 0) return 'Skip';
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
}

/**
 * Calculates total and saved time for a batch of results.
 * Returns: { totalIfReadAll, totalAfterTriage, savedMinutes, savedHours }
 */
export function calculateBatchTime(results: (EvaluationResult & { error?: string })[]) {
  const valid = results.filter(r => !r.error) as EvaluationResult[];

  // Time if user read every paper in full
  const totalIfReadAll = valid.reduce((sum, r) => {
    const wpm = 220;
    return sum + Math.round(r.stats.word_count / wpm);
  }, 0);

  // Time after triage (only reading what's recommended)
  const totalAfterTriage = valid.reduce((sum, r) => {
    return sum + estimateReadingTime(r).minutes;
  }, 0);

  const savedMinutes = Math.max(0, totalIfReadAll - totalAfterTriage);

  return {
    totalIfReadAll,
    totalAfterTriage,
    savedMinutes,
    totalIfReadAllLabel: formatMinutes(totalIfReadAll),
    totalAfterTriageLabel: formatMinutes(totalAfterTriage),
    savedLabel: formatMinutes(savedMinutes),
  };
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function triggerDownload(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function getFormattedDate(): string {
  return new Date().toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
  });
}

function cleanFilename(filename: string): string {
  return filename.replace(/\.pdf$/i, '').replace(/[^\w\s-]/g, '').trim();
}

// ---------------------------------------------------------------------------
// Markdown export (improved)
// ---------------------------------------------------------------------------

export function exportMarkdown(
  results: (EvaluationResult & { error?: string })[]
): void {
  const valid = results.filter(r => !r.error) as EvaluationResult[];
  const sorted = [...valid].sort((a, b) => (b.overall ?? 0) - (a.overall ?? 0));
  const timeStats = calculateBatchTime(results);
  const date = getFormattedDate();

  const lines: string[] = [
    '# Desk Reject — Reading Plan',
    '',
    `> Generated: ${date}`,
    `> Papers evaluated: ${valid.length}`,
    `> Time to read everything: ${timeStats.totalIfReadAllLabel}`,
    `> Time after triage: **${timeStats.totalAfterTriageLabel}**`,
    `> **You saved: ${timeStats.savedLabel}**`,
    '',
    '---',
    '',
    '## Quick Reference',
    '',
    '| # | Paper | Verdict | Score | Read | Est. Time |',
    '|---|-------|---------|-------|------|-----------|',
  ];

  sorted.forEach((r, i) => {
    const verdict = VERDICT_LABELS[r.verdict as Verdict] || r.verdict;
    const whatToRead = WHAT_TO_READ_SHORT[r.what_to_read as WhatToRead] || r.what_to_read;
    const time = estimateReadingTime(r);
    lines.push(
      `| ${i + 1} | ${cleanFilename(r.filename)} | ${verdict} | ${r.overall?.toFixed(1)} | ${whatToRead} | ${time.label} |`
    );
  });

  lines.push('', '---', '', '## Detailed Notes', '');

  sorted.forEach((r, i) => {
    const verdict = VERDICT_LABELS[r.verdict as Verdict] || r.verdict;
    const type = PAPER_TYPE_LABELS[r.detected_paper_type as PaperType] || r.detected_paper_type;
    const whatToRead = WHAT_TO_READ_LABELS[r.what_to_read as WhatToRead] || r.what_to_read;
    const time = estimateReadingTime(r);

    lines.push(`### ${i + 1}. ${cleanFilename(r.filename)}`);
    lines.push('');
    lines.push(`- **Verdict:** ${verdict} · **Score:** ${r.overall?.toFixed(1)}/5 · **Est. time:** ${time.hoursLabel}`);
    lines.push(`- **Type:** ${type} · **Priority:** ${r.priority}`);
    lines.push(`- **What to read:** ${whatToRead}`);
    lines.push('');
    lines.push(`> ${r.thirty_second_take}`);
    lines.push('');
    lines.push(`*${r.what_to_read_reasoning}*`);
    lines.push('');
    lines.push('| Dimension | Score | Notes |');
    lines.push('|-----------|-------|-------|');
    lines.push(`| Readability | ${r.readability?.score?.toFixed(1)} | ${r.readability?.reasoning} |`);
    lines.push(`| Technical Rigor | ${r.technical_rigor?.score?.toFixed(1)} | ${r.technical_rigor?.reasoning} |`);
    lines.push(`| Novelty | ${r.novelty?.score?.toFixed(1)} | ${r.novelty?.reasoning} |`);
    lines.push(`| Writing Quality | ${r.writing_quality?.score?.toFixed(1)} | ${r.writing_quality?.reasoning} |`);
    lines.push(`| Vocabulary | ${r.vocabulary_profile?.score?.toFixed(1)} | ${r.vocabulary_profile?.reasoning} |`);
    lines.push(`| Signal/Noise | ${r.signal_to_noise?.score?.toFixed(1)} | ${r.signal_to_noise?.reasoning} |`);
    lines.push('');
    lines.push('- [ ] Read');
    lines.push('- [ ] Notes taken');
    lines.push('- [ ] Added to bibliography');
    lines.push('');
    lines.push('---');
    lines.push('');
  });

  triggerDownload(
    lines.join('\n'),
    `desk-reject-reading-plan-${Date.now()}.md`,
    'text/markdown'
  );
}

// Backwards compatibility alias
export const exportReadingPlan = exportMarkdown;

// ---------------------------------------------------------------------------
// Obsidian vault export (ZIP of individual .md files)
// ---------------------------------------------------------------------------

export async function exportObsidianVault(
  results: (EvaluationResult & { error?: string })[]
): Promise<void> {
  const valid = results.filter(r => !r.error) as EvaluationResult[];
  const sorted = [...valid].sort((a, b) => (b.overall ?? 0) - (a.overall ?? 0));
  const timeStats = calculateBatchTime(results);
  const date = new Date().toISOString().split('T')[0];

  const zip = new JSZip();
  const folder = zip.folder('desk-reject-vault')!;

  // INDEX file — overview of all papers
  const indexLines: string[] = [
    '---',
    'tags: [desk-reject, reading-list]',
    `date: ${date}`,
    `papers: ${valid.length}`,
    `time-saved: "${timeStats.savedLabel}"`,
    '---',
    '',
    '# Reading List Index',
    '',
    `**Generated:** ${getFormattedDate()}`,
    `**Papers:** ${valid.length}`,
    `**Time after triage:** ${timeStats.totalAfterTriageLabel} (saved ${timeStats.savedLabel})`,
    '',
    '## Papers',
    '',
  ];

  sorted.forEach((r, i) => {
    const time = estimateReadingTime(r);
    const verdict = VERDICT_LABELS[r.verdict as Verdict];
    indexLines.push(`${i + 1}. [[${cleanFilename(r.filename)}]] — ${verdict} · ${r.overall?.toFixed(1)}/5 · ${time.label}`);
  });

  folder.file('_index.md', indexLines.join('\n'));

  // Individual paper files
  sorted.forEach(r => {
    const time = estimateReadingTime(r);
    const name = cleanFilename(r.filename);
    const verdict = VERDICT_LABELS[r.verdict as Verdict];
    const type = PAPER_TYPE_LABELS[r.detected_paper_type as PaperType];
    const whatToRead = WHAT_TO_READ_LABELS[r.what_to_read as WhatToRead];

    const paperLines: string[] = [
      '---',
      `title: "${name}"`,
      `verdict: "${verdict}"`,
      `score: ${r.overall?.toFixed(1)}`,
      `type: "${type}"`,
      `priority: "${r.priority}"`,
      `what-to-read: "${WHAT_TO_READ_SHORT[r.what_to_read as WhatToRead]}"`,
      `reading-time: "${time.hoursLabel}"`,
      `readability: ${r.readability?.score?.toFixed(1)}`,
      `rigor: ${r.technical_rigor?.score?.toFixed(1)}`,
      `novelty: ${r.novelty?.score?.toFixed(1)}`,
      `writing: ${r.writing_quality?.score?.toFixed(1)}`,
      `signal-noise: ${r.signal_to_noise?.score?.toFixed(1)}`,
      `pages: ${r.stats.page_count}`,
      `words: ${r.stats.word_count}`,
      `tags: [desk-reject, ${r.verdict}, ${r.detected_paper_type}]`,
      `date-evaluated: ${date}`,
      `status: unread`,
      '---',
      '',
      `# ${name}`,
      '',
      '## Summary',
      '',
      r.thirty_second_take,
      '',
      `**What to read:** ${whatToRead}`,
      `*${r.what_to_read_reasoning}*`,
      '',
      '## Scores',
      '',
      '| Dimension | Score | Notes |',
      '|-----------|-------|-------|',
      `| Readability | ${r.readability?.score?.toFixed(1)} | ${r.readability?.reasoning} |`,
      `| Technical Rigor | ${r.technical_rigor?.score?.toFixed(1)} | ${r.technical_rigor?.reasoning} |`,
      `| Novelty | ${r.novelty?.score?.toFixed(1)} | ${r.novelty?.reasoning} |`,
      `| Writing Quality | ${r.writing_quality?.score?.toFixed(1)} | ${r.writing_quality?.reasoning} |`,
      `| Vocabulary | ${r.vocabulary_profile?.score?.toFixed(1)} | ${r.vocabulary_profile?.reasoning} |`,
      `| Signal/Noise | ${r.signal_to_noise?.score?.toFixed(1)} | ${r.signal_to_noise?.reasoning} |`,
      '',
      '## Flags',
      '',
      `- **Readability:** ${r.readability?.flag}`,
      `- **Rigor:** ${r.technical_rigor?.flag}`,
      `- **Novelty:** ${r.novelty?.flag}`,
      `- **Writing:** ${r.writing_quality?.flag}`,
      '',
      '## My Notes',
      '',
      '*Add your notes here...*',
      '',
      '## Reading Checklist',
      '',
      '- [ ] Read',
      '- [ ] Key claims noted',
      '- [ ] Methods assessed',
      '- [ ] Added to Zotero/bibliography',
      '- [ ] Connected to other papers',
    ];

    folder.file(`${name}.md`, paperLines.join('\n'));
  });

  const blob = await zip.generateAsync({ type: 'blob' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `desk-reject-obsidian-${Date.now()}.zip`;
  a.click();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// CSV export (Excel / Sheets / systematic review)
// ---------------------------------------------------------------------------

export function exportCSV(
  results: (EvaluationResult & { error?: string })[]
): void {
  const valid = results.filter(r => !r.error) as EvaluationResult[];
  const sorted = [...valid].sort((a, b) => (b.overall ?? 0) - (a.overall ?? 0));

  const escape = (val: string | number | undefined): string => {
    if (val === undefined || val === null) return '';
    const str = String(val);
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const headers = [
    'Rank', 'Filename', 'Verdict', 'Overall', 'Type', 'Priority',
    'What to Read', 'Est. Time (min)',
    'Readability', 'Technical Rigor', 'Novelty', 'Writing Quality', 'Vocabulary', 'Signal/Noise',
    'Pages', 'Words', 'Equations', 'References',
    '30-Second Take', 'What to Read Reasoning',
    'Readability Notes', 'Rigor Notes', 'Novelty Notes',
  ];

  const rows = sorted.map((r, i) => {
    const time = estimateReadingTime(r);
    return [
      i + 1,
      escape(cleanFilename(r.filename)),
      escape(VERDICT_LABELS[r.verdict as Verdict]),
      r.overall?.toFixed(1),
      escape(PAPER_TYPE_SHORT[r.detected_paper_type as PaperType]),
      escape(r.priority),
      escape(WHAT_TO_READ_SHORT[r.what_to_read as WhatToRead]),
      time.minutes,
      r.readability?.score?.toFixed(1),
      r.technical_rigor?.score?.toFixed(1),
      r.novelty?.score?.toFixed(1),
      r.writing_quality?.score?.toFixed(1),
      r.vocabulary_profile?.score?.toFixed(1),
      r.signal_to_noise?.score?.toFixed(1),
      r.stats.page_count,
      r.stats.word_count,
      r.stats.equation_count,
      r.stats.reference_count,
      escape(r.thirty_second_take),
      escape(r.what_to_read_reasoning),
      escape(r.readability?.reasoning),
      escape(r.technical_rigor?.reasoning),
      escape(r.novelty?.reasoning),
    ].join(',');
  });

  const csv = [headers.join(','), ...rows].join('\n');
  triggerDownload(csv, `desk-reject-${Date.now()}.csv`, 'text/csv');
}

// ---------------------------------------------------------------------------
// Notion CSV export
// Notion database import expects specific column formatting.
// ---------------------------------------------------------------------------

export function exportNotionCSV(
  results: (EvaluationResult & { error?: string })[]
): void {
  const valid = results.filter(r => !r.error) as EvaluationResult[];
  const sorted = [...valid].sort((a, b) => (b.overall ?? 0) - (a.overall ?? 0));

  const escape = (val: string | number | undefined): string => {
    if (val === undefined || val === null) return '';
    const str = String(val);
    return `"${str.replace(/"/g, '""')}"`;
  };

  // Notion imports Select fields as plain text
  const headers = [
    'Name',           // Title field
    'Verdict',        // Select
    'Score',          // Number
    'Type',           // Select
    'What to Read',   // Select
    'Est. Time',      // Text
    'Readability',    // Number
    'Rigor',          // Number
    'Novelty',        // Number
    'Writing',        // Number
    'Signal/Noise',   // Number
    'Priority',       // Select
    'Status',         // Select — for tracking reading progress
    'Summary',        // Text
    'Pages',          // Number
    'Words',          // Number
  ];

  const rows = sorted.map(r => {
    const time = estimateReadingTime(r);
    return [
      escape(cleanFilename(r.filename)),
      escape(VERDICT_LABELS[r.verdict as Verdict]),
      r.overall?.toFixed(1),
      escape(PAPER_TYPE_SHORT[r.detected_paper_type as PaperType]),
      escape(WHAT_TO_READ_SHORT[r.what_to_read as WhatToRead]),
      escape(time.hoursLabel),
      r.readability?.score?.toFixed(1),
      r.technical_rigor?.score?.toFixed(1),
      r.novelty?.score?.toFixed(1),
      r.writing_quality?.score?.toFixed(1),
      r.signal_to_noise?.score?.toFixed(1),
      escape(r.priority),
      escape('Unread'),     // Default status
      escape(r.thirty_second_take),
      r.stats.page_count,
      r.stats.word_count,
    ].join(',');
  });

  const csv = [headers.join(','), ...rows].join('\n');
  triggerDownload(csv, `desk-reject-notion-${Date.now()}.csv`, 'text/csv');
}

// ---------------------------------------------------------------------------
// Single paper export (for Results page)
// ---------------------------------------------------------------------------

export function exportSingleMarkdown(result: EvaluationResult): void {
  exportMarkdown([result]);
}

export function exportSingleCSV(result: EvaluationResult): void {
  exportCSV([result]);
}